// src/components/layout/Header.tsx
//
// Top navigation header. Logo + nav links + language switcher
// + account badge (when signed in) or Sign in button. Sticky
// at top of the viewport.
//
// Reacts to AuthContext state — when the user signs in or out,
// the header updates accordingly.

import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogIn, ShieldCheck, User, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { supportedLanguages, type SupportedLanguage } from "@/i18n/helper";
import { useAuth } from "@/components/auth/AuthContext";
import type { AccountType } from "@/lib/auth";
import { openAgent } from "@/lib/agent/agentControl";
import { NotificationBell } from "@/components/notifications/NotificationBell";

const languageLabels: Record<SupportedLanguage, string> = {
  en: "EN",
  fr: "FR",
  nl: "NL",
};

/**
 * Account badge component (used in header).
 * Shows the current account type with an icon.
 */
function AccountBadge({ accountType }: { accountType: AccountType }) {
  if (accountType === "anonymous") return null;
  const variant: Record<
    Exclude<AccountType, "anonymous">,
    "success" | "teal" | "info"
  > = {
    demo: "teal",
    admin: "success",
    customer: "info",
    auditor: "teal",
    "marketing-sales": "teal",
    sales: "teal",
    legal: "info",
    management: "info",
  };
  const Icon =
    accountType === "admin"
      ? ShieldCheck
      : accountType === "auditor"
        ? ShieldCheck
        : User;
  const label: Record<Exclude<AccountType, "anonymous">, string> = {
    admin: "Admin",
    demo: "Demo",
    customer: "Customer",
    auditor: "Auditor",
    "marketing-sales": "Marketing",
    sales: "Sales",
    legal: "Legal",
    management: "Management",
  };
  return (
    <Badge variant={variant[accountType]} size="sm" className={cn("hidden md:inline-flex", "gap-1")}>
      <Icon className="h-3 w-3" strokeWidth={1.5} />
      {label[accountType]}
    </Badge>
  );
}

export function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = (i18n.language as SupportedLanguage) || "en";
  const auth = useAuth();

  const navItems = [
    { to: "/catalog", label: t("nav.catalog") },
    { to: "/stores", label: t("nav.stores") },
  { to: "/portal", label: t("nav.portal") },
  ];

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-bg-canvas/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex h-8 items-center justify-center">
            <img
              src="/benchmark-logo-mark.png"
              alt="BENCHMARK AV Distribution"
              draggable={false}
              className="h-7 w-auto select-none"
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(67%) sepia(56%) " +
                  "saturate(475%) hue-rotate(132deg) brightness(89%) contrast(91%)",
              }}
            />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-sm font-510 tracking-tight text-fg-primary">
              {t("app.name")}
            </span>
            <span className="text-[10px] font-510 uppercase tracking-wider text-benchmark-red">
              Distribution
            </span>
          </div>
        </Link>

        {/* Main nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "rounded px-3 py-1.5 text-sm font-510 transition-colors",
                  isActive
                    ? "bg-surface-elevated text-fg-primary"
                    : "text-fg-secondary hover:bg-surface-elevated hover:text-fg-primary",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right side: auth status + language */}
        <div className="flex items-center gap-2">
          {/* Agent entry — visible to all, but only "alive" when signed in */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (auth.isAuthenticated) {
                openAgent();
              } else {
                navigate("/login");
              }
            }}
            aria-label="Ask Teddy"
            title={
              auth.isAuthenticated
                ? "Open Teddy (\u2318K / Ctrl+K)"
                : "Sign in to chat with Teddy"
            }
            className="hidden items-center gap-1 sm:inline-flex"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span className="text-xs">Ask Teddy</span>
          </Button>

          {/* Account badge (only visible when signed in) */}
          <AccountBadge accountType={auth.accountType} />

          {/* Language switcher */}
          <div className="hidden items-center rounded border border-border bg-bg-panel p-0.5 sm:flex">
            {supportedLanguages.map((lang: SupportedLanguage) => (
              <button
                key={lang}
                onClick={() => void i18n.changeLanguage(lang)}
                aria-pressed={currentLang === lang}
                aria-label={`Switch language to ${languageLabels[lang]}`}
                className={cn(
                  "h-7 rounded px-2 text-xs font-510 transition-colors",
                  currentLang === lang
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "text-fg-tertiary hover:text-fg-primary",
                )}
              >
                {languageLabels[lang]}
              </button>
            ))}
          </div>

          {/* Auth: show username if signed in, otherwise Sign in button */}
          {auth.isAuthenticated ? (
            <>
              <NotificationBell isAuthenticated={auth.isAuthenticated} />
              <NavLink
                to="/login"
                className="hidden max-w-[140px] items-center truncate rounded px-3 py-1.5 text-sm font-510 text-fg-secondary transition-colors hover:bg-surface-elevated hover:text-fg-primary sm:flex"
                title={auth.user?.fullName || auth.user?.username || t("auth.signIn")}
              >
                {auth.user?.fullName || auth.user?.username || t("auth.signIn")}
              </NavLink>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              href="/login"
              className="hidden sm:inline-flex"
            >
              <LogIn className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("nav.login")}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
